/**
 * Document-keyed localStorage for Review findings and party identification.
 * Legacy unkeyed blobs (version !== 2) are ignored so findings never leak
 * across documents.
 */

import {
  documentIdentityKey,
  type DocumentIdentity,
} from './reviewDocumentIdentity';

export type ReviewResultsStored = {
  ourSide?: string[];
  theirSide?: string[];
  optionalDetails?: string;
  hasRedlines?: boolean;
  sideAssignments?: Record<string, 'our_side' | 'their_side'>;
};

export const REVIEW_STATE_VERSION = 2;
export const REVIEW_STATE_TTL_MS = 24 * 60 * 60 * 1000;
export const REVIEW_STATE_MAX_DOCUMENTS = 10;

export type ReviewCompleteStored = ReviewResultsStored & {
  identity: DocumentIdentity;
  timestamp: string;
  reviewFindings: {
    critical: unknown[];
    warning: unknown[];
    info: unknown[];
  };
  documentParagraphs: Array<{ index: number; text: string }>;
};

type PartyStored = ReviewResultsStored & {
  identity: DocumentIdentity;
  timestamp: string;
};

type V2Store<T> = {
  version: typeof REVIEW_STATE_VERSION;
  byDocument: Record<string, T>;
};

function getLocalStorage(): Storage | null {
  const root = globalThis as { window?: { localStorage?: Storage } };
  try {
    return root.window?.localStorage ?? null;
  } catch {
    return null;
  }
}

function isFresh(timestamp: string | undefined, now: number): boolean {
  if (!timestamp) return false;
  const t = new Date(timestamp).getTime();
  if (!Number.isFinite(t)) return false;
  return now - t < REVIEW_STATE_TTL_MS;
}

export function pruneReviewEntries<T extends { timestamp?: string }>(
  byDocument: Record<string, T>,
  now: number = Date.now(),
): Record<string, T> {
  const fresh = Object.entries(byDocument).filter(([, entry]) => isFresh(entry?.timestamp, now));
  fresh.sort(
    (a, b) =>
      new Date(b[1].timestamp || 0).getTime() - new Date(a[1].timestamp || 0).getTime(),
  );
  return Object.fromEntries(fresh.slice(0, REVIEW_STATE_MAX_DOCUMENTS));
}

function readStore<T extends { timestamp?: string }>(storageKey: string): Record<string, T> {
  const storage = getLocalStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<V2Store<T>>;
    if (parsed?.version === REVIEW_STATE_VERSION && parsed.byDocument && typeof parsed.byDocument === 'object') {
      return pruneReviewEntries(parsed.byDocument);
    }
  } catch {
    /* ignore corrupt / legacy blobs */
  }
  return {};
}

function writeStore<T extends { timestamp?: string }>(
  storageKey: string,
  byDocument: Record<string, T>,
): void {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    const pruned = pruneReviewEntries(byDocument);
    const payload: V2Store<T> = { version: REVIEW_STATE_VERSION, byDocument: pruned };
    storage.setItem(storageKey, JSON.stringify(payload));
  } catch (err) {
    console.error(`Error writing ${storageKey}:`, err);
  }
}

function lookup<T extends { timestamp?: string }>(
  storageKey: string,
  identity: DocumentIdentity,
): T | null {
  const key = documentIdentityKey(identity);
  if (!key) return null;
  return readStore<T>(storageKey)[key] ?? null;
}

function upsert<T extends { timestamp?: string }>(
  storageKey: string,
  identity: DocumentIdentity,
  entry: T,
): void {
  const key = documentIdentityKey(identity);
  if (!key) return;
  const byDocument = readStore<T>(storageKey);
  byDocument[key] = entry;
  writeStore(storageKey, byDocument);
}

function removeKey(storageKey: string, identity: DocumentIdentity): void {
  const key = documentIdentityKey(identity);
  if (!key) return;
  const byDocument = readStore<{ timestamp?: string }>(storageKey);
  if (!(key in byDocument)) return;
  delete byDocument[key];
  writeStore(storageKey, byDocument);
}

export function loadReviewCompleteState(
  identity: DocumentIdentity,
  storageKey: string,
): ReviewCompleteStored | null {
  const entry = lookup<ReviewCompleteStored>(storageKey, identity);
  if (!entry?.reviewFindings || !entry.documentParagraphs) return null;
  return entry;
}

export function saveReviewCompleteState(
  identity: DocumentIdentity,
  storageKey: string,
  state: Omit<ReviewCompleteStored, 'identity' | 'timestamp'> & { timestamp?: string },
): void {
  upsert<ReviewCompleteStored>(storageKey, identity, {
    ...state,
    identity,
    timestamp: state.timestamp || new Date().toISOString(),
  });
}

export function loadReviewPartyResults(
  identity: DocumentIdentity,
  storageKey: string,
): PartyStored | null {
  return lookup<PartyStored>(storageKey, identity);
}

export function saveReviewPartyResults(
  identity: DocumentIdentity,
  storageKey: string,
  results: ReviewResultsStored,
): void {
  upsert<PartyStored>(storageKey, identity, {
    ...results,
    identity,
    timestamp: new Date().toISOString(),
  });
}

export function clearReviewStateForIdentity(
  identity: DocumentIdentity,
  completeStateKey: string,
  partyResultsKey: string,
): void {
  removeKey(completeStateKey, identity);
  removeKey(partyResultsKey, identity);
}
